import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import { getAgents, saveAgent, deleteAgent, db } from './firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// KONFIGURASI TELEGRAM BOT
const TELEGRAM_BOT_TOKEN = "8571888481:AAGdqdiaa4kPd_YWqfZauELZ1-ACiMV0iYg";
const TELEGRAM_CHAT_ID = "-1003574594574";

// ==================== HELPER FORMATTING & UTILS ====================
const formatRP = (val) => new Intl.NumberFormat('id-ID', { 
    style: 'currency', currency: 'IDR', maximumFractionDigits: 0 
}).format(val || 0);

const formatInputWithDots = (val) => {
    if (!val && val !== 0) return '';
    let value = val.toString().replace(/\D/g, ''); 
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const parseRawNumber = (val) => {
    if (!val) return 0;
    return parseInt(val.toString().replace(/\D/g, ''), 10) || 0;
};

// Membersihkan object dari nilai undefined/null agar Firebase tidak error
const sanitizePayload = (obj) => {
    const cleaned = {};
    Object.keys(obj).forEach(key => {
        if (obj[key] !== undefined && obj[key] !== null) {
            cleaned[key] = obj[key];
        } else {
            cleaned[key] = '';
        }
    });
    return cleaned;
};

// Helper Menghitung Rentang Tanggal Senin-Minggu Secara Otomatis Berdasarkan Waktu Lokal Saat Ini
// PERUBAHAN: Jika hari ini adalah Minggu (dayOfWeek === 0), otomatis maju ke hari Senin minggu depan
const getWeeklyDates = () => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Minggu, 1 = Senin, ...
    
    // Jika hari Minggu (0), tanggal langsung berubah ke Minggu berikutnya (Senin besok hingga Minggu depan)
    const distanceToMonday = dayOfWeek === 0 ? 1 : 1 - dayOfWeek;
    
    const monday = new Date(now);
    monday.setDate(now.getDate() + distanceToMonday);

    const days = ['senin', 'selasa', 'rabu', 'kamis', 'jumat', 'sabtu', 'minggu'];
    const result = {};

    days.forEach((day, index) => {
        const currentDate = new Date(monday);
        currentDate.setDate(monday.getDate() + index);
        
        const dd = String(currentDate.getDate()).padStart(2, '0');
        const mm = String(currentDate.getMonth() + 1).padStart(2, '0');
        const yyyy = currentDate.getFullYear();
        
        result[day] = `${dd}/${mm}/${yyyy}`;
    });

    return result;
};

// ==================== UI KIT (ICON, TOKEN, MODAL) - TAMPILAN SAJA ====================
// Semua icon memakai gaya yang sama: outline 24x24, stroke 1.75, ujung membulat.
const ICON_PATHS = {
    users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    userPlus: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" x2="19" y1="8" y2="14" /><line x1="22" x2="16" y1="11" y2="11" /></>,
    wallet: <><path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" /><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" /></>,
    calendar: <><path d="M8 2v4" /><path d="M16 2v4" /><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M3 10h18" /></>,
    lock: <><rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
    plus: <><path d="M5 12h14" /><path d="M12 5v14" /></>,
    search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>,
    edit: <><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" /><path d="m15 5 4 4" /></>,
    trash: <><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></>,
    message: <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />,
    phone: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />,
    download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></>,
    send: <><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></>,
    clock: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
    sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2" /><path d="M12 20v2" /><path d="m4.93 4.93 1.41 1.41" /><path d="m17.66 17.66 1.41 1.41" /><path d="M2 12h2" /><path d="M20 12h2" /><path d="m6.34 17.66-1.41 1.41" /><path d="m19.07 4.93-1.41 1.41" /></>,
    sunset: <><path d="M12 10V2" /><path d="m4.93 10.93 1.41 1.41" /><path d="M2 18h2" /><path d="M20 18h2" /><path d="m19.07 10.93-1.41 1.41" /><path d="M22 22H2" /><path d="m16 6-4 4-4-4" /><path d="M16 18a4 4 0 0 0-8 0" /></>,
    moon: <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />,
    coffee: <><path d="M10 2v2" /><path d="M14 2v2" /><path d="M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a4 4 0 1 1 0 8h-1" /><path d="M6 2v2" /></>,
    trendingUp: <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></>,
    coins: <><circle cx="8" cy="8" r="6" /><path d="M18.09 10.37A6 6 0 1 1 10.34 18" /><path d="M7 6h1v4" /><path d="m16.71 13.88.7.71-2.82 2.82" /></>,
    heart: <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />,
    landmark: <><line x1="3" x2="21" y1="22" y2="22" /><line x1="6" x2="6" y1="18" y2="11" /><line x1="10" x2="10" y1="18" y2="11" /><line x1="14" x2="14" y1="18" y2="11" /><line x1="18" x2="18" y1="18" y2="11" /><polygon points="12 2 20 7 4 7" /></>,
    sheet: <><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M8 13h2" /><path d="M14 13h2" /><path d="M8 17h2" /><path d="M14 17h2" /></>,
    x: <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>,
    calculator: <><rect width="16" height="20" x="4" y="2" rx="2" /><line x1="8" x2="16" y1="6" y2="6" /><line x1="16" x2="16" y1="14" y2="18" /><path d="M16 10h.01" /><path d="M12 10h.01" /><path d="M8 10h.01" /><path d="M12 14h.01" /><path d="M8 14h.01" /><path d="M12 18h.01" /><path d="M8 18h.01" /></>,
    briefcase: <><path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /><rect width="20" height="14" x="2" y="6" rx="2" /></>,
    gauge: <><path d="m12 14 4-4" /><path d="M3.34 19a10 10 0 1 1 17.32 0" /></>,
    inbox: <><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></>,
    layers: <><path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" /><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" /><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" /></>,
};

const Icon = memo(({ name, className = 'w-4 h-4', strokeWidth = 1.75 }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`${className} shrink-0`}
        aria-hidden="true"
    >
        {ICON_PATHS[name]}
    </svg>
));

// Token gaya bersama agar semua input/label/tombol konsisten
const UI_INPUT = "w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 outline-none transition-all duration-200 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 placeholder:text-slate-300 placeholder:font-normal";
const UI_LABEL = "block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5";
const UI_CELL_INPUT = "w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-slate-800 tabular-nums outline-none transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10";
const UI_ICON_BTN = "inline-flex items-center justify-center w-8 h-8 rounded-lg border border-transparent text-slate-400 transition-all duration-200 active:scale-90";

const MODAL_TONE = {
    blue: { tile: 'bg-blue-50 text-blue-600', btn: 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/20' },
    amber: { tile: 'bg-amber-50 text-amber-600', btn: 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/20' },
    emerald: { tile: 'bg-emerald-50 text-emerald-600', btn: 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20' },
    purple: { tile: 'bg-purple-50 text-purple-600', btn: 'bg-purple-600 hover:bg-purple-700 shadow-purple-600/20' },
};

const ModalShell = ({ z = 'z-[100]', tone = 'blue', icon, title, subtitle, onClose, maxW = 'max-w-md', children }) => (
    <div className={`fixed inset-0 bg-slate-900/50 backdrop-blur-sm ${z} flex items-center justify-center p-4 da-fade`}>
        <div className={`bg-white w-full ${maxW} rounded-3xl shadow-2xl border border-slate-100 da-pop max-h-[92vh] overflow-y-auto`}>
            <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${MODAL_TONE[tone].tile}`}>
                        <Icon name={icon} className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-base font-bold text-slate-800 leading-tight">{title}</h3>
                        {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Tutup"
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all duration-200 active:scale-90"
                >
                    <Icon name="x" className="w-4 h-4" />
                </button>
            </div>
            <div className="p-6">{children}</div>
        </div>
    </div>
);

const ModalFooter = ({ onCancel, submitLabel, disabled, tone = 'blue' }) => (
    <div className="flex gap-3 pt-2">
        <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-600 py-3 rounded-xl font-semibold text-sm transition-all duration-200 active:scale-95"
        >
            Batal
        </button>
        <button
            type="submit"
            disabled={disabled}
            className={`flex-1 text-white py-3 rounded-xl font-semibold text-sm shadow-lg transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${MODAL_TONE[tone].btn}`}
        >
            {submitLabel}
        </button>
    </div>
);

const SectionHeader = ({ icon, title, subtitle, tone = 'blue', children }) => (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-4 min-w-0">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${MODAL_TONE[tone].tile}`}>
                <Icon name={icon} className="w-5 h-5" />
            </div>
            <div className="min-w-0">
                <h2 className="text-base sm:text-lg font-bold text-slate-800 leading-tight">{title}</h2>
                <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{subtitle}</p>
            </div>
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">{children}</div>
    </div>
);

const EmptyState = ({ icon = 'inbox', text }) => (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-slate-400">
        <div className="w-11 h-11 rounded-2xl bg-slate-50 flex items-center justify-center">
            <Icon name={icon} className="w-5 h-5" />
        </div>
        <p className="text-xs font-medium">{text}</p>
    </div>
);

// ==================== SUB-COMPONENTS ====================

const AgentSkeleton = memo(() => (
    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm animate-pulse">
        <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-slate-100 rounded-xl"></div>
            <div className="space-y-2 flex-1">
                <div className="h-3.5 bg-slate-200 rounded w-28"></div>
                <div className="h-3 bg-slate-100 rounded w-20"></div>
            </div>
        </div>
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-2">
            <div className="h-3 bg-slate-200 rounded w-16"></div>
            <div className="h-6 bg-slate-300 rounded w-32"></div>
        </div>
        <div className="mt-4 flex justify-between items-center">
            <div className="h-8 bg-slate-100 rounded-lg w-24"></div>
            <div className="h-8 bg-slate-100 rounded-lg w-16"></div>
        </div>
    </div>
));

const AgentCard = memo(({ agent, onEdit, onDelete }) => (
    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg hover:border-blue-200 hover:-translate-y-0.5 transition-all duration-300 group da-fade-up">
        <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 font-bold text-sm flex items-center justify-center uppercase group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                {(agent.name || '?').toString().trim().charAt(0) || '?'}
            </div>
            <div className="min-w-0 flex-1">
                <h3 className="font-bold text-slate-800 uppercase text-[13px] leading-tight truncate">{agent.name}</h3>
                <p className="text-xs text-slate-400 font-medium flex items-center gap-1 mt-0.5 truncate">
                    <Icon name="phone" className="w-3 h-3" />
                    <span className="truncate">{agent.phone || '-'}</span>
                </p>
            </div>
        </div>

        <div className="bg-slate-50 px-4 py-3.5 rounded-xl border border-slate-100 group-hover:border-blue-100 group-hover:bg-blue-50/40 transition-colors duration-300">
            <p className="text-[11px] text-slate-400 uppercase font-semibold tracking-wide mb-0.5">Sisa Saldo</p>
            <p className="text-xl font-bold text-blue-600 tabular-nums">{formatRP(agent.balance)}</p>
        </div>

        <div className="mt-4 flex justify-between items-center gap-2">
            <a
                href={`https://wa.me/${agent.phone ? agent.phone.replace(/^0/, '62') : ''}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white text-xs font-semibold transition-all duration-200 active:scale-95"
            >
                <Icon name="message" className="w-3.5 h-3.5" />
                WhatsApp
            </a>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => onEdit(agent)}
                    title="Edit agen"
                    aria-label="Edit agen"
                    className={`${UI_ICON_BTN} hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100`}
                >
                    <Icon name="edit" className="w-4 h-4" />
                </button>
                <button
                    onClick={() => onDelete(agent.id)}
                    title="Hapus agen"
                    aria-label="Hapus agen"
                    className={`${UI_ICON_BTN} hover:bg-red-50 hover:text-red-600 hover:border-red-100`}
                >
                    <Icon name="trash" className="w-4 h-4" />
                </button>
            </div>
        </div>
    </div>
));

const OpTableRow = memo(({ item, onPersenChange, onEdit, onDelete, matchingKasbon }) => {
    const hasKasbon = matchingKasbon && matchingKasbon.nominal > 0;

    return (
        <tr className="hover:bg-slate-50/80 transition-colors duration-150">
            <td className="py-3 px-4 align-middle">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 font-bold text-xs flex items-center justify-center uppercase">
                        {(item.name || '?').toString().trim().charAt(0) || '?'}
                    </div>
                    <span className="uppercase font-bold text-slate-800 text-[13px]">{item.name}</span>
                </div>
            </td>
            <td className="py-3 px-2 text-center align-middle">
                <div className="inline-flex items-center gap-1">
                    <input
                        type="text"
                        className="w-14 text-center bg-amber-50 border border-amber-200 rounded-lg font-bold text-sm py-1.5 text-amber-900 outline-none focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-400/10 transition-all duration-200"
                        value={item.persen !== undefined ? item.persen : ''}
                        placeholder="0"
                        onChange={(e) => onPersenChange(item.id, e.target.value)}
                    />
                    <span className="text-xs font-semibold text-amber-700">%</span>
                </div>
            </td>
            <td className="py-3 px-4 text-right align-middle">
                <div className="font-bold text-slate-900 tabular-nums text-sm">{formatRP(item.computedNet)}</div>
                {hasKasbon && (
                    <div className="text-[11px] text-red-500 font-medium mt-0.5 tabular-nums">
                        Potong kasbon: -{formatRP(matchingKasbon.nominal)}
                    </div>
                )}
            </td>
            <td className="py-3 px-3 align-middle">
                <div className="flex justify-end items-center gap-1">
                    <button
                        onClick={() => onEdit(item)}
                        title="Edit"
                        aria-label="Edit"
                        className={`${UI_ICON_BTN} hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100`}
                    >
                        <Icon name="edit" className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => onDelete(item.id)}
                        title="Hapus"
                        aria-label="Hapus"
                        className={`${UI_ICON_BTN} hover:bg-red-50 hover:text-red-600 hover:border-red-100`}
                    >
                        <Icon name="trash" className="w-4 h-4" />
                    </button>
                </div>
            </td>
        </tr>
    );
});

const SHIFT_STYLE = {
    Pagi: { label: 'Pagi', icon: 'sun', pill: 'bg-blue-50 text-blue-700 border-blue-200' },
    Siang: { label: 'Siang', icon: 'sunset', pill: 'bg-amber-50 text-amber-700 border-amber-200' },
    Malam: { label: 'Malam', icon: 'moon', pill: 'bg-indigo-900 text-indigo-50 border-indigo-900' },
    Libur: { label: 'Libur', icon: 'coffee', pill: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const ShiftBadge = memo(({ shift, times }) => {
    let key = 'Libur';
    if (shift === 'Pagi') key = 'Pagi';
    else if (shift === 'Siang' || shift === 'Sore') key = 'Siang';
    else if (shift === 'Malam') key = 'Malam';

    const style = SHIFT_STYLE[key];
    const time = key === 'Pagi' ? times.pagi
        : key === 'Siang' ? (times.siang || times.sore)
        : key === 'Malam' ? times.malam
        : times.libur;

    return (
        <div className="flex flex-col items-center gap-1">
            <span className={`inline-flex items-center gap-1 whitespace-nowrap px-2.5 py-1 rounded-full border text-[11px] font-semibold ${style.pill}`}>
                <Icon name={style.icon} className="w-3 h-3" />
                {style.label}
            </span>
            <span className="text-[10px] text-slate-400 font-medium leading-tight">{time}</span>
        </div>
    );
});

const JADWAL_DAYS = [
    { key: 'senin', label: 'SENIN', th: '', lbl: 'text-blue-300', inp: 'bg-slate-700 text-blue-200 border-slate-600 focus:ring-blue-400', td: '' },
    { key: 'selasa', label: 'SELASA', th: '', lbl: 'text-blue-300', inp: 'bg-slate-700 text-blue-200 border-slate-600 focus:ring-blue-400', td: '' },
    { key: 'rabu', label: 'RABU', th: '', lbl: 'text-blue-300', inp: 'bg-slate-700 text-blue-200 border-slate-600 focus:ring-blue-400', td: '' },
    { key: 'kamis', label: 'KAMIS', th: '', lbl: 'text-blue-300', inp: 'bg-slate-700 text-blue-200 border-slate-600 focus:ring-blue-400', td: '' },
    { key: 'jumat', label: 'JUMAT', th: '', lbl: 'text-blue-300', inp: 'bg-slate-700 text-blue-200 border-slate-600 focus:ring-blue-400', td: '' },
    { key: 'sabtu', label: 'SABTU', th: 'bg-amber-900/40', lbl: 'text-amber-300', inp: 'bg-amber-950 text-amber-200 border-amber-800 focus:ring-amber-400', td: 'bg-amber-50/50' },
    { key: 'minggu', label: 'MINGGU', th: 'bg-red-900/40', lbl: 'text-red-300', inp: 'bg-red-950 text-red-200 border-red-800 focus:ring-red-400', td: 'bg-red-50/50' },
];

// ==================== KOMPONEN UTAMA ====================
export default function DaftarAgen({ totalProfit = 0 }) {
    // REF UNTUK EKSPOR JADWAL KE GAMBAR
    const jadwalContainerRef = useRef(null);

    // KALKULASI OTOMATIS TANGGAL MINGGUAN SAAT INI
    const weeklyDates = useMemo(() => getWeeklyDates(), []);

    // STATE AGENT
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSendingTelegram, setIsSendingTelegram] = useState(false);
    const [formData, setFormData] = useState({ id: '', name: '', phone: '', balance: '' });

    // SECURITY PIN & NAVIGATION
    const [activeMainTab, setActiveMainTab] = useState('utama'); 
    const [isPinUnlocked, setIsPinUnlocked] = useState(false);
    const [showPinModal, setShowPinModal] = useState(false);
    const [inputPin, setInputPin] = useState('');

    // OPERASIONAL, FINANSIAL & SALARY
    const [manualLaba, setManualLaba] = useState(0);
    const [manualModalBulanDepan, setManualModalBulanDepan] = useState(0);
    const [laporanOperasional, setLaporanOperasional] = useState([]);
    
    // CONFIG KASBON BARU TERBAGI (DEFAULT 45.000.000)
    const [kasbonSkema, setKasbonSkema] = useState({
        totalKasbonBaru: 45000000,
        jumlahPembagi: 3,
        jumlahCicilan: 6
    });

    // KASBON INISIAL I F Y
    const [kasbonList, setKasbonList] = useState([
        { id: 'k1', nama: 'I', nominal: 0 },
        { id: 'k2', nama: 'F', nominal: 0 },
        { id: 'k3', nama: 'Y', nominal: 0 }
    ]);

    const [isOpModalOpen, setIsOpModalOpen] = useState(false);
    const [isOpEditing, setIsOpEditing] = useState(false);
    const [opFormData, setOpFormData] = useState({
        id: '',
        name: '',
        persen: 0,
        nominal: 0
    });

    // SALARY KARYAWAN TETAP
    const [karyawanSalaryList, setKaryawanSalaryList] = useState([
        { id: 's1', nama: 'FARID', hariKerja: 27, ratePerHari: 25000, bonusPersen: 1 }
    ]);
    const [isSalaryModalOpen, setIsSalaryModalOpen] = useState(false);
    const [salaryFormData, setSalaryFormData] = useState({
        id: '',
        nama: '',
        hariKerja: 26,
        ratePerHari: 25000,
        bonusPersen: 0.5
    });

    // JADWAL KERJA & SHIFT CONFIG
    const [jadwalKerjaList, setJadwalKerjaList] = useState([
        { id: 'j4', nama: 'FARID', senin: 'Pagi', selasa: 'Pagi', rabu: 'Pagi', kamis: 'Pagi', jumat: 'Pagi', sabtu: 'Libur', minggu: 'Libur' }
    ]);

    // STATE UNTUK EDIT TANGGAL MANUAL MINGGUAN (DIPERBARUI KE FIREBASE)
    const [customDates, setCustomDates] = useState({
        senin: '', selasa: '', rabu: '', kamis: '', jumat: '', sabtu: '', minggu: ''
    });

    const [isJadwalModalOpen, setIsJadwalModalOpen] = useState(false);
    const [jadwalFormData, setJadwalFormData] = useState({
        id: '', nama: '', senin: 'Pagi', selasa: 'Pagi', rabu: 'Pagi', kamis: 'Pagi', jumat: 'Pagi', sabtu: 'Libur', minggu: 'Libur'
    });

    // SHIFT CONFIG TERBARU (DIPERBARUI KE FIREBASE)
    const [shiftTimes, setShiftTimes] = useState({
        pagi: '06.00 - 15.00 WIB',
        siang: '12.00 - 21.00 WIB',
        malam: '23.00 - 08.00 WIB',
        libur: 'OFF / Free Day'
    });
    const [isShiftConfigModalOpen, setIsShiftConfigModalOpen] = useState(false);

    // DELETE CONFIRMATION MODAL STATE
    const [deleteModal, setDeleteModal] = useState({
        show: false, title: '', message: '', onConfirm: null
    });

    const openDeleteConfirm = useCallback((title, message, onConfirm) => {
        setDeleteModal({ show: true, title, message, onConfirm });
    }, []);

    const closeDeleteConfirm = useCallback(() => {
        setDeleteModal({ show: false, title: '', message: '', onConfirm: null });
    }, []);

    // Toast System
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const showToast = useCallback((message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
    }, []);

    // HELPER AUTO-SAVE CONFIG GLOBAL KE CLOUD PERMANEN (FIREBASE)
    const saveGlobalConfigToCloud = useCallback((newLaba, newModal, newKasbonSkema) => {
        saveAgent(sanitizePayload({
            id: 'global_financial_config',
            manualLaba: newLaba !== undefined ? newLaba : manualLaba,
            manualModalBulanDepan: newModal !== undefined ? newModal : manualModalBulanDepan,
            kasbonSkema: newKasbonSkema !== undefined ? newKasbonSkema : kasbonSkema,
            type: 'global_config'
        })).catch(err => console.error("Auto save global config error:", err));
    }, [manualLaba, manualModalBulanDepan, kasbonSkema]);

    // SYNC LABA OTOMATIS DARI REKAPAN.JS (FIRESTORE SNAPSHOT HARI INI)
    useEffect(() => {
        const todayStr = new Date().toISOString().split('T')[0];
        const docRef = doc(db, "rekapan", `rekapan_${todayStr}`);

        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data && data.labaBersih !== undefined) {
                    setManualLaba(data.labaBersih);
                    saveGlobalConfigToCloud(data.labaBersih, undefined, undefined);
                }
            }
        }, (err) => {
            console.error("Gagal mendengarkan rekapan harian:", err);
        });

        return () => unsubscribe();
    }, [saveGlobalConfigToCloud]);

    // FETCH DATA UTAMA DARI FIREBASE
    const loadAllCloudData = useCallback(async () => {
        try {
            setLoading(true);
            const allCloudData = await getAgents();

            const mainAgents = [];
            const opList = [];
            const cloudKasbon = [];
            const cloudSalary = [];
            const cloudJadwal = [];
            let cloudShiftConfig = null;
            let cloudCustomDates = null;
            let cloudGlobalConfig = null;

            if (Array.isArray(allCloudData)) {
                for (let i = 0; i < allCloudData.length; i++) {
                    const item = allCloudData[i];
                    if (!item.type || item.type === 'agent') mainAgents.push(item);
                    else if (item.type === 'laporan_operasional') opList.push(item);
                    else if (item.type === 'kasbon_karyawan') cloudKasbon.push(item);
                    else if (item.type === 'salary_karyawan') cloudSalary.push(item);
                    else if (item.type === 'jadwal_kerja') {
                        const normalizedJadwal = { ...item };
                        ['senin', 'selasa', 'rabu', 'kamis', 'jumat', 'sabtu', 'minggu'].forEach(h => {
                            if (normalizedJadwal[h] === 'Sore') normalizedJadwal[h] = 'Siang';
                        });
                        cloudJadwal.push(normalizedJadwal);
                    }
                    else if (item.type === 'shift_config') cloudShiftConfig = item;
                    else if (item.type === 'custom_dates') cloudCustomDates = item;
                    else if (item.type === 'global_config') cloudGlobalConfig = item;
                }
            }

            if (cloudGlobalConfig) {
                if (cloudGlobalConfig.manualLaba !== undefined) setManualLaba(cloudGlobalConfig.manualLaba);
                if (cloudGlobalConfig.manualModalBulanDepan !== undefined) setManualModalBulanDepan(cloudGlobalConfig.manualModalBulanDepan);
                if (cloudGlobalConfig.kasbonSkema) setKasbonSkema(cloudGlobalConfig.kasbonSkema);
            }

            if (opList.length === 0) {
                const defaultOpData = [
                    { id: '1', name: 'I', persen: 5, nominal: 0, type: 'laporan_operasional' },
                    { id: '2', name: 'F', persen: 5, nominal: 0, type: 'laporan_operasional' },
                    { id: '3', name: 'Y', persen: 5, nominal: 0, type: 'laporan_operasional' },
                    { id: '4', name: 'MODAL', persen: 25, nominal: 0, type: 'laporan_operasional' },
                    { id: '5', name: 'INVEST', persen: 20, nominal: 0, type: 'laporan_operasional' },
                    { id: '6', name: 'SEDEKAH', persen: 5, nominal: 0, type: 'laporan_operasional' },
                    { id: '7', name: 'KARYAWAN', persen: 5, nominal: 0, type: 'laporan_operasional' }
                ];
                setLaporanOperasional(defaultOpData);
            } else {
                setLaporanOperasional(opList);
            }

            if (cloudKasbon.length > 0) setKasbonList(cloudKasbon);
            if (cloudSalary.length > 0) setKaryawanSalaryList(cloudSalary);
            if (cloudJadwal.length > 0) setJadwalKerjaList(cloudJadwal);
            
            if (cloudShiftConfig && cloudShiftConfig.pagi) {
                setShiftTimes({
                    pagi: cloudShiftConfig.pagi || '06.00 - 15.00 WIB',
                    siang: cloudShiftConfig.siang || cloudShiftConfig.sore || '12.00 - 21.00 WIB',
                    malam: cloudShiftConfig.malam || '23.00 - 08.00 WIB',
                    libur: cloudShiftConfig.libur || 'OFF / Free Day'
                });
            }

            if (cloudCustomDates && cloudCustomDates.senin) {
                setCustomDates({
                    senin: cloudCustomDates.senin || '',
                    selasa: cloudCustomDates.selasa || '',
                    rabu: cloudCustomDates.rabu || '',
                    kamis: cloudCustomDates.kamis || '',
                    jumat: cloudCustomDates.jumat || '',
                    sabtu: cloudCustomDates.sabtu || '',
                    minggu: cloudCustomDates.minggu || ''
                });
            }

            setAgents(mainAgents);
        } catch (error) {
            console.error("Gagal memuat data dari Cloud:", error);
            showToast("Gagal memuat data dari Cloud", "error");
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        loadAllCloudData();

        const handleBeforeUnload = () => {
            setIsPinUnlocked(false);
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [loadAllCloudData]);

    useEffect(() => {
        if (totalProfit && totalProfit > 0) {
            setManualLaba(totalProfit);
        }
    }, [totalProfit]);

    // MERGE TANGGAL DENGAN FALLBACK OTOMATIS
    const displayDates = useMemo(() => {
        return {
            senin: customDates.senin || weeklyDates.senin,
            selasa: customDates.selasa || weeklyDates.selasa,
            rabu: customDates.rabu || weeklyDates.rabu,
            kamis: customDates.kamis || weeklyDates.kamis,
            jumat: customDates.jumat || weeklyDates.jumat,
            sabtu: customDates.sabtu || weeklyDates.sabtu,
            minggu: customDates.minggu || weeklyDates.minggu
        };
    }, [customDates, weeklyDates]);

    // AUTO-SAVE FIREBASE UNTUK TANGGAL MANUAL MINGGUAN
    const handleCustomDateChange = (day, value) => {
        setCustomDates(prev => {
            const updated = { ...prev, [day]: value };
            saveAgent(sanitizePayload({ id: 'config_custom_dates', ...updated, type: 'custom_dates' }))
                .catch(err => console.error("Auto save custom dates error:", err));
            return updated;
        });
    };

    // CALCULATIONS
    const grandTotalSaldo = useMemo(() => {
        return agents.reduce((sum, a) => sum + (parseInt(a.balance, 10) || 0), 0);
    }, [agents]);

    const kasbonMap = useMemo(() => {
        const map = new Map();
        kasbonList.forEach(k => map.set(k.nama.toUpperCase(), k));
        return map;
    }, [kasbonList]);

    const computedLaporanOperasional = useMemo(() => {
        return laporanOperasional.map(item => {
            let gross = 0;
            if (item.persen && item.persen > 0) {
                gross = (manualLaba * item.persen) / 100;
            } else {
                gross = item.nominal || 0;
            }

            const matchingKasbon = kasbonMap.get(item.name.toUpperCase());
            const computedNet = matchingKasbon ? Math.max(0, gross - (matchingKasbon.nominal || 0)) : gross;

            return {
                ...item,
                computedGross: gross,
                computedNet
            };
        });
    }, [laporanOperasional, manualLaba, kasbonMap]);

    const totalKasbon = useMemo(() => {
        return kasbonList.reduce((sum, k) => sum + (parseInt(k.nominal, 10) || 0), 0);
    }, [kasbonList]);

    const hasilKasbonPerOrangTotal = useMemo(() => {
        const divider = kasbonSkema.jumlahPembagi || 1;
        return (kasbonSkema.totalKasbonBaru || 0) / divider;
    }, [kasbonSkema]);

    const hasilCicilanPerOrangPerBayar = useMemo(() => {
        const cicilan = kasbonSkema.jumlahCicilan || 1;
        return hasilKasbonPerOrangTotal / cicilan;
    }, [hasilKasbonPerOrangTotal, kasbonSkema]);

    const getItemCalculatedVal = useCallback((name, defaultPersen = 0) => {
        const item = computedLaporanOperasional.find(i => i.name.toUpperCase() === name.toUpperCase());
        if (item) return item.computedNet;
        return defaultPersen > 0 ? (manualLaba * defaultPersen) / 100 : 0;
    }, [computedLaporanOperasional, manualLaba]);

    const gajiI = useMemo(() => getItemCalculatedVal('I', 5), [getItemCalculatedVal]);
    const gajiF = useMemo(() => getItemCalculatedVal('F', 5), [getItemCalculatedVal]);
    const gajiY = useMemo(() => getItemCalculatedVal('Y', 5), [getItemCalculatedVal]);

    const totalGajiInisialNett = useMemo(() => gajiI + gajiF + gajiY, [gajiI, gajiF, gajiY]);
    const totalGajiInisialGross = useMemo(() => (manualLaba * 15 / 100), [manualLaba]);

    const nominalSedekah = useMemo(() => getItemCalculatedVal('SEDEKAH', 5), [getItemCalculatedVal]);
    const nominalInvestasi = useMemo(() => getItemCalculatedVal('INVEST', 20), [getItemCalculatedVal]);
    const nominalAlokasiKaryawan = useMemo(() => (manualLaba * 5) / 100, [manualLaba]);
    const tambahModal = useMemo(() => (manualLaba * 25) / 100, [manualLaba]);

    const totalSemuaKaryawanDiterima = useMemo(() => {
        return karyawanSalaryList.reduce((sum, k) => {
            const gapok = (k.hariKerja || 0) * (k.ratePerHari || 0);
            const bonus = (manualLaba * (k.bonusPersen || 0)) / 100;
            return sum + gapok + bonus;
        }, 0);
    }, [karyawanSalaryList, manualLaba]);

    const sisaAlokasiKasKaryawan = useMemo(() => {
        return nominalAlokasiKaryawan - totalSemuaKaryawanDiterima;
    }, [nominalAlokasiKaryawan, totalSemuaKaryawanDiterima]);

    const filteredAgents = useMemo(() => {
        if (!searchTerm) return agents;
        const term = searchTerm.toLowerCase();
        return agents.filter(a => a.name ? a.name.toLowerCase().includes(term) : false);
    }, [agents, searchTerm]);

    // PRO EXPORT EXCEL DENGAN STYLING LENGKAP VIA EXCELJS
    const handleExportExcel = async () => {
        try {
            showToast("Mempersiapkan Laporan Excel Berwarna...", "warning");

            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'Sistem Agen Pro';
            workbook.created = new Date();

            // SHEET 1: FINANSIAL & OPERASIONAL
            const wsFin = workbook.addWorksheet('Finansial & Operasional');
            wsFin.views = [{ showGridLines: true }];

            // Judul Header
            wsFin.mergeCells('A1:D1');
            const titleCell = wsFin.getCell('A1');
            titleCell.value = 'LAPORAN OPERASIONAL & FINANSIAL';
            titleCell.font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FFFFFF' } };
            titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1E293B' } };
            titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
            wsFin.getRow(1).height = 30;

            // Sub Header Laba Utama
            wsFin.mergeCells('A2:B2');
            wsFin.getCell('A2').value = 'TOTAL LABA UTAMA (Acuan Rekapan)';
            wsFin.getCell('A2').font = { name: 'Arial', size: 10, bold: true };
            wsFin.getCell('A2').alignment = { horizontal: 'right', vertical: 'middle' };

            wsFin.mergeCells('C2:D2');
            const labaCell = wsFin.getCell('C2');
            labaCell.value = manualLaba;
            labaCell.numFmt = '"Rp "#,##0';
            labaCell.font = { name: 'Arial', size: 11, bold: true, color: { argb: '065F46' } };
            labaCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D1FAE5' } };
            labaCell.alignment = { horizontal: 'right', vertical: 'middle' };
            wsFin.getRow(2).height = 24;

            // Header Tabel Operasional
            const headersOp = ['Nama / Inisial', 'Persentase', 'Nominal Gross', 'Nominal Net (Setelah Kasbon)'];
            const headerRowOp = wsFin.addRow(headersOp);
            headerRowOp.height = 22;
            headerRowOp.eachCell((cell) => {
                cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '334155' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            });

            // Isi Data Operasional
            computedLaporanOperasional.forEach((item, index) => {
                const row = wsFin.addRow([
                    item.name,
                    (item.persen || 0) / 100,
                    item.computedGross || 0,
                    item.computedNet || 0
                ]);
                row.height = 20;

                const isZebra = index % 2 === 1;
                const bgColor = isZebra ? 'F8FAFC' : 'FFFFFF';

                row.eachCell((cell, colNum) => {
                    cell.font = { name: 'Arial', size: 10 };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'E2E8F0' } },
                        bottom: { style: 'thin', color: { argb: 'E2E8F0' } },
                        left: { style: 'thin', color: { argb: 'E2E8F0' } },
                        right: { style: 'thin', color: { argb: 'E2E8F0' } }
                    };

                    if (colNum === 1) {
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    } else if (colNum === 2) {
                        cell.numFmt = '0.0%';
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    } else {
                        cell.numFmt = '"Rp "#,##0';
                        cell.alignment = { horizontal: 'right', vertical: 'middle' };
                    }
                });
            });

            wsFin.addRow([]); // Spacer

            // Header Ringkasan Finansial Tambahan
            const sumHeaderRow = wsFin.addRow(['PARAMETER FINANSIAL', '', '', 'NILAI / KET']);
            wsFin.mergeCells(`A${sumHeaderRow.number}:C${sumHeaderRow.number}`);
            sumHeaderRow.height = 22;
            sumHeaderRow.getCell(1).font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
            sumHeaderRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1E293B' } };
            sumHeaderRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
            sumHeaderRow.getCell(4).font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
            sumHeaderRow.getCell(4).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1E293B' } };
            sumHeaderRow.getCell(4).alignment = { horizontal: 'right', vertical: 'middle' };

            const summaryItems = [
                ['Total Gaji (I, F, Y) Gross (15%)', totalGajiInisialGross],
                ['Total Gaji (I, F, Y) Net', totalGajiInisialNett],
                ['Sedekah (5%)', nominalSedekah],
                ['Investasi (20%)', nominalInvestasi],
                ['Tambah Modal (25%)', tambahModal],
                ['Modal Bulan Depan', manualModalBulanDepan],
                ['Beban Kasbon Baru Per Orang', hasilKasbonPerOrangTotal],
                ['Cicilan Kasbon Baru Per Bayar', hasilCicilanPerOrangPerBayar]
            ];

            summaryItems.forEach(([label, value]) => {
                const row = wsFin.addRow([label, '', '', value]);
                wsFin.mergeCells(`A${row.number}:C${row.number}`);
                row.height = 20;
                row.getCell(1).font = { name: 'Arial', size: 10, bold: true };
                row.getCell(4).font = { name: 'Arial', size: 10, bold: true, color: { argb: '1E3A8A' } };
                row.getCell(4).numFmt = '"Rp "#,##0';
                row.getCell(4).alignment = { horizontal: 'right', vertical: 'middle' };

                for (let c = 1; c <= 4; c++) {
                    row.getCell(c).border = {
                        top: { style: 'thin', color: { argb: 'E2E8F0' } },
                        bottom: { style: 'thin', color: { argb: 'E2E8F0' } }
                    };
                }
            });

            // Set Lebar Kolom Auto Sheet 1
            wsFin.columns = [
                { width: 22 },
                { width: 16 },
                { width: 22 },
                { width: 30 }
            ];


            // SHEET 2: SALARY KARYAWAN
            const wsSal = workbook.addWorksheet('Salary Karyawan');
            wsSal.views = [{ showGridLines: true }];

            wsSal.mergeCells('A1:G1');
            const titleSal = wsSal.getCell('A1');
            titleSal.value = 'RINCIAN SALARY KARYAWAN TETAP & TAMBAHAN';
            titleSal.font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FFFFFF' } };
            titleSal.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '581C87' } };
            titleSal.alignment = { horizontal: 'center', vertical: 'middle' };
            wsSal.getRow(1).height = 30;

            const headersSal = ['Nama Karyawan', 'Hari Kerja', 'Rate Harian', 'Subtotal Gapok', 'Bonus Laba (%)', 'Subtotal Bonus', 'Total Diterima'];
            const headerRowSal = wsSal.addRow(headersSal);
            headerRowSal.height = 22;
            headerRowSal.eachCell((cell) => {
                cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '6B21A8' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            });

            karyawanSalaryList.forEach((k, idx) => {
                const gapok = (k.hariKerja || 0) * (k.ratePerHari || 0);
                const bonus = (manualLaba * (k.bonusPersen || 0)) / 100;
                const total = gapok + bonus;

                const row = wsSal.addRow([
                    k.nama,
                    k.hariKerja,
                    k.ratePerHari || 0,
                    gapok,
                    (k.bonusPersen || 0) / 100,
                    bonus,
                    total
                ]);
                row.height = 20;

                const isZebra = idx % 2 === 1;
                const bgColor = isZebra ? 'F3E8FF' : 'FFFFFF';

                row.eachCell((cell, colNum) => {
                    cell.font = { name: 'Arial', size: 10 };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'E9D5FF' } },
                        bottom: { style: 'thin', color: { argb: 'E9D5FF' } },
                        left: { style: 'thin', color: { argb: 'E9D5FF' } },
                        right: { style: 'thin', color: { argb: 'E9D5FF' } }
                    };

                    if (colNum === 1) {
                        cell.font = { name: 'Arial', size: 10, bold: true };
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    } else if (colNum === 2) {
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    } else if (colNum === 5) {
                        cell.numFmt = '0.0%';
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    } else if (colNum === 7) {
                        cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '581C87' } };
                        cell.numFmt = '"Rp "#,##0';
                        cell.alignment = { horizontal: 'right', vertical: 'middle' };
                    } else {
                        cell.numFmt = '"Rp "#,##0';
                        cell.alignment = { horizontal: 'right', vertical: 'middle' };
                    }
                });
            });

            wsSal.columns = [
                { width: 22 },
                { width: 14 },
                { width: 18 },
                { width: 20 },
                { width: 16 },
                { width: 20 },
                { width: 22 }
            ];

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `Laporan_Lengkap_Operasional_${new Date().toISOString().split('T')[0]}.xlsx`);

            showToast("Berhasil mengekspor data Excel Profesional!", "success");
        } catch (error) {
            console.error("Gagal export excel:", error);
            showToast("Gagal mengekspor file Excel: " + error.message, "error");
        }
    };

    // EXPORT TELEGRAM
    const handleSendJadwalToTelegram = async () => {
        if (!jadwalContainerRef.current) {
            showToast("Elemen jadwal tidak ditemukan!", "error");
            return;
        }

        const cleanBotToken = TELEGRAM_BOT_TOKEN.trim();
        const cleanChatId = TELEGRAM_CHAT_ID.trim();

        if (!cleanBotToken || !cleanChatId) {
            showToast("Bot Token / Chat ID belum diatur dengan benar!", "error");
            return;
        }

        try {
            setIsSendingTelegram(true);
            showToast("Mempersiapkan gambar jadwal utuh...", "warning");

            let html2canvas;
            try {
                html2canvas = (await import('html2canvas')).default;
            } catch (err) {
                console.error("html2canvas belum diinstall:", err);
                showToast("Sistem membutuhkan library 'html2canvas'. Jalankan `npm install html2canvas`", "error");
                setIsSendingTelegram(false);
                return;
            }

            const element = jadwalContainerRef.current;

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                backgroundColor: "#ffffff",
                scrollX: 0,
                scrollY: 0,
                windowWidth: 1400,
                ignoreElements: (el) => el.classList.contains('no-export'),
                onclone: (clonedDoc) => {
                    const dateInputs = clonedDoc.querySelectorAll('.date-header-input');
                    dateInputs.forEach((input) => {
                        const val = input.value || input.placeholder || '';
                        const textSpan = clonedDoc.createElement('span');
                        textSpan.innerText = val;
                        textSpan.className = "text-blue-200 font-mono text-xs font-bold block text-center mt-1";
                        if (input.parentNode) {
                            input.parentNode.replaceChild(textSpan, input);
                        }
                    });
                }
            });

            canvas.toBlob(async (blob) => {
                if (!blob) {
                    showToast("Gagal membuat file gambar", "error");
                    setIsSendingTelegram(false);
                    return;
                }

                const formDataToSend = new FormData();
                formDataToSend.append("chat_id", cleanChatId);
                formDataToSend.append("photo", blob, "jadwal_kerja.png");
                formDataToSend.append("caption", `📅 JADWAL KERJA STAFF TERBARU\nPeriode: ${displayDates.senin} s/d ${displayDates.minggu}\n\nDikirim otomatis dari Sistem Management Agen`);

                const directUrl = `https://api.telegram.org/bot${cleanBotToken}/sendPhoto`;

                try {
                    let response = await fetch(directUrl, {
                        method: 'POST',
                        body: formDataToSend
                    });

                    if (!response.ok && response.status !== 401) {
                        const proxyUrl = `https://corsproxy.io/?` + encodeURIComponent(directUrl);
                        response = await fetch(proxyUrl, {
                            method: 'POST',
                            body: formDataToSend
                        });
                    }

                    const resData = await response.json();

                    if (resData.ok) {
                        showToast("Berhasil mengirim jadwal ke Telegram!", "success");
                    } else {
                        console.error("Telegram API Error:", resData);
                        showToast("Gagal kirim Telegram: " + (resData.description || "Terjadi kesalahan API"), "error");
                    }
                } catch (apiErr) {
                    console.error("Network Error Telegram API:", apiErr);
                    showToast("Gagal koneksi ke Telegram. Periksa koneksi jaringan Anda.", "error");
                } finally {
                    setIsSendingTelegram(false);
                }
            }, 'image/png');

        } catch (error) {
            console.error("Error sending image to Telegram:", error);
            showToast("Gagal mengirim ke Telegram: " + error.message, "error");
            setIsSendingTelegram(false);
        }
    };

    const handleSwitchTab = useCallback((tab) => {
        if (tab === 'utama') {
            setActiveMainTab('utama');
        } else {
            if (isPinUnlocked) {
                setActiveMainTab(tab);
            } else {
                setShowPinModal(true);
            }
        }
    }, [isPinUnlocked]);

    const handleVerifyPin = useCallback((e) => {
        e.preventDefault();
        if (inputPin === '111222') {
            setIsPinUnlocked(true);
            setShowPinModal(false);
            setInputPin('');
            setActiveMainTab('karyawan');
            showToast("Akses Diberikan", "success");
        } else {
            showToast("PIN Salah!", "error");
            setInputPin('');
        }
    }, [inputPin, showToast]);

    // AUTO-SAVE PERSEN LAPORAN OPERASIONAL SEKERET
    const handlePersenChange = useCallback(async (id, newPersenStr) => {
        const newPersen = parseFloat(newPersenStr.replace(',', '.')) || 0;
        setLaporanOperasional(prev => {
            const updated = prev.map(item => item.id === id ? { ...item, persen: newPersen } : item);
            const targetItem = updated.find(item => item.id === id);
            if (targetItem) {
                saveAgent(sanitizePayload({ ...targetItem, type: 'laporan_operasional' }))
                    .catch(err => console.error("Auto save persen error:", err));
            }
            return updated;
        });
    }, []);

    const handleSaveOpData = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const cleanNominal = parseRawNumber(opFormData.nominal);
        const cleanPersen = parseFloat(opFormData.persen.toString().replace(',', '.')) || 0;
        const idToUse = opFormData.id || 'op_' + Date.now();

        try {
            const payload = sanitizePayload({
                ...opFormData,
                id: idToUse,
                name: opFormData.name.toUpperCase(),
                persen: cleanPersen,
                nominal: cleanNominal,
                type: 'laporan_operasional'
            });
            await saveAgent(payload);
            
            setLaporanOperasional(prev => {
                const exists = prev.some(item => item.id === payload.id);
                if (exists) return prev.map(item => item.id === payload.id ? payload : item);
                return [...prev, payload];
            });

            setIsOpModalOpen(false);
            showToast("Data operasional diperbarui ke cloud", "success");
            setOpFormData({
                id: '',
                name: '',
                persen: 0,
                nominal: 0
            });
        } catch (error) {
            console.error("Error saving op data:", error);
            showToast("Gagal menyimpan data operasional: " + error.message, "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteOpData = useCallback((id) => {
        openDeleteConfirm(
            "Hapus Baris Operasional",
            "Apakah Anda yakin ingin menghapus baris laporan operasional ini?",
            async () => {
                try {
                    await deleteAgent(id);
                    setLaporanOperasional(prev => prev.filter(i => i.id !== id));
                    showToast("Baris laporan berhasil dihapus", "success");
                } catch (error) {
                    setLaporanOperasional(prev => prev.filter(i => i.id !== id));
                    showToast("Baris laporan dihapus dari tampilan", "warning");
                }
            }
        );
    }, [openDeleteConfirm, showToast]);

    // AUTO-SAVE PERUBAHAN NOMINAL KASBON KARYAWAN
    const handleKasbonNominalChange = useCallback((id, rawValue) => {
        const cleanNominal = parseRawNumber(rawValue);
        setKasbonList(prev => {
            const updated = prev.map(item => item.id === id ? { ...item, nominal: cleanNominal } : item);
            const targetItem = updated.find(item => item.id === id);
            if (targetItem) {
                saveAgent(sanitizePayload({ ...targetItem, type: 'kasbon_karyawan' }))
                    .then(() => showToast("Kasbon tersimpan otomatis", "success"))
                    .catch(err => console.error("Auto save kasbon error:", err));
            }
            return updated;
        });
    }, [showToast]);

    const handleDeleteKasbon = useCallback((id) => {
        openDeleteConfirm(
            "Hapus Data Kasbon",
            "Apakah Anda yakin ingin menghapus catatan kasbon ini?",
            async () => {
                try {
                    await deleteAgent(id);
                    setKasbonList(prev => prev.filter(k => k.id !== id));
                    showToast("Data kasbon berhasil dihapus", "success");
                } catch (error) {
                    setKasbonList(prev => prev.filter(k => k.id !== id));
                    showToast("Data kasbon dihapus", "warning");
                }
            }
        );
    }, [openDeleteConfirm, showToast]);

    const handleSaveJadwal = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const idToUse = jadwalFormData.id || 'jadwal_' + Date.now();
        const payload = sanitizePayload({
            ...jadwalFormData,
            id: idToUse,
            nama: jadwalFormData.nama.toUpperCase(),
            type: 'jadwal_kerja'
        });

        try {
            await saveAgent(payload);
            setIsJadwalModalOpen(false);
            
            setJadwalKerjaList(prev => {
                const exists = prev.some(item => item.id === idToUse);
                if (exists) return prev.map(item => item.id === idToUse ? payload : item);
                return [...prev, payload];
            });

            showToast("Jadwal kerja berhasil disimpan", "success");
            setJadwalFormData({
                id: '', nama: '', senin: 'Pagi', selasa: 'Pagi', rabu: 'Pagi', kamis: 'Pagi', jumat: 'Pagi', sabtu: 'Libur', minggu: 'Libur'
            });
        } catch (error) {
            console.error("Error saving schedule:", error);
            showToast("Gagal menyimpan jadwal kerja: " + error.message, "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteJadwal = useCallback((id) => {
        openDeleteConfirm(
            "Hapus Jadwal Kerja",
            "Apakah Anda yakin ingin menghapus jadwal kerja karyawan ini?",
            async () => {
                try {
                    await deleteAgent(id);
                    setJadwalKerjaList(prev => prev.filter(j => j.id !== id));
                    showToast("Jadwal kerja dihapus", "success");
                } catch (error) {
                    setJadwalKerjaList(prev => prev.filter(j => j.id !== id));
                    showToast("Jadwal dihapus (lokal)", "warning");
                }
            }
        );
    }, [openDeleteConfirm, showToast]);

    const handleSaveShiftConfig = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const shiftPayload = sanitizePayload({ 
                id: 'config_shift_time', 
                pagi: shiftTimes.pagi,
                siang: shiftTimes.siang || shiftTimes.sore,
                malam: shiftTimes.malam,
                libur: shiftTimes.libur,
                type: 'shift_config' 
            });
            await saveAgent(shiftPayload);
            setIsShiftConfigModalOpen(false);
            showToast("Pengaturan Shift Diperbarui di Cloud", "success");
        } catch (error) {
            console.error("Gagal simpan shift ke cloud:", error);
            setIsShiftConfigModalOpen(false);
            showToast("Shift Diperbarui (Lokal)", "warning");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSaveSalaryKaryawan = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const idToUse = salaryFormData.id || 'sal_' + Date.now();
        const payload = sanitizePayload({
            id: idToUse,
            nama: salaryFormData.nama.toUpperCase(),
            hariKerja: parseInt(salaryFormData.hariKerja, 10) || 0,
            ratePerHari: parseRawNumber(salaryFormData.ratePerHari),
            bonusPersen: parseFloat(salaryFormData.bonusPersen) || 0,
            type: 'salary_karyawan'
        });

        try {
            await saveAgent(payload);
            
            setKaryawanSalaryList(prev => {
                const exists = prev.some(k => k.id === idToUse);
                if (exists) return prev.map(k => k.id === idToUse ? payload : k);
                return [...prev, payload];
            });

            setIsSalaryModalOpen(false);
            setSalaryFormData({ id: '', nama: '', hariKerja: 26, ratePerHari: 25000, bonusPersen: 0.5 });
            showToast("Salary karyawan diperbarui", "success");
        } catch (error) {
            console.error("Error saving salary:", error);
            showToast("Gagal menyimpan data salary karyawan: " + error.message, "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteSalaryKaryawan = useCallback((id) => {
        openDeleteConfirm(
            "Hapus Data Karyawan",
            "Apakah Anda yakin ingin menghapus karyawan ini dari rincian salary?",
            async () => {
                try {
                    await deleteAgent(id);
                    setKaryawanSalaryList(prev => prev.filter(k => k.id !== id));
                    showToast("Salary karyawan dihapus", "success");
                } catch (error) {
                    setKaryawanSalaryList(prev => prev.filter(k => k.id !== id));
                    showToast("Salary karyawan dihapus", "warning");
                }
            }
        );
    }, [openDeleteConfirm, showToast]);

    const handleSaveAgent = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const cleanBalance = parseRawNumber(formData.balance);
        const idToUse = (isEditing && formData.id) ? formData.id : 'agent_' + Date.now();
        
        const payload = sanitizePayload({ 
            ...formData, 
            id: idToUse, 
            balance: cleanBalance, 
            type: 'agent' 
        });

        try {
            await saveAgent(payload);
            setIsModalOpen(false);
            setFormData({ id: '', name: '', phone: '', balance: '' });
            setIsEditing(false);
            showToast(isEditing ? "Data Agen Berhasil Diperbarui" : "Data Agen Baru Berhasil Disimpan", "success");
            await loadAllCloudData();
        } catch (error) {
            console.error("Gagal simpan agen:", error);
            showToast("Gagal menyimpan data ke Cloud: " + error.message, "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteAgent = useCallback((id) => {
        openDeleteConfirm(
            "Hapus Agen",
            "Apakah Anda yakin ingin menghapus data agen ini?",
            async () => {
                try {
                    await deleteAgent(id);
                    showToast("Agen Berhasil Dihapus", "success");
                    await loadAllCloudData();
                } catch (err) {
                    showToast("Gagal menghapus agen dari Cloud", "error");
                }
            }
        );
    }, [loadAllCloudData, openDeleteConfirm, showToast]);

    const handleOpenEditAgent = useCallback((agent) => {
        setIsEditing(true); 
        setFormData({ ...agent, balance: formatInputWithDots(agent.balance) }); 
        setIsModalOpen(true); 
    }, []);

    const handleOpenEditOp = useCallback((item) => {
        setIsOpEditing(true);
        setOpFormData({ 
            ...item, 
            nominal: formatInputWithDots(item.nominal) 
        });
        setIsOpModalOpen(true);
    }, []);

    return (
        <div className="max-w-7xl mx-auto space-y-6 p-4 font-sans relative">
            
            {/* TOAST NOTIFICATION */}
            {toast.show && (
                <div className={`fixed bottom-5 right-5 z-[200] px-5 py-3 rounded-2xl shadow-xl font-bold text-xs flex items-center gap-2 text-white transition-all duration-300 transform translate-y-0 ${
                    toast.type === 'error' ? 'bg-red-600' : toast.type === 'warning' ? 'bg-amber-500' : 'bg-emerald-600'
                }`}>
                    <span>{toast.message}</span>
                </div>
            )}

            {/* ANIMASI RINGAN (hanya transform/opacity, otomatis mati jika user memilih reduced motion) */}
            <style>{`
                @keyframes daFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
                @keyframes daFade { from { opacity: 0; } to { opacity: 1; } }
                @keyframes daPop { from { opacity: 0; transform: translateY(8px) scale(.97); } to { opacity: 1; transform: none; } }
                .da-fade-up { animation: daFadeUp .35s cubic-bezier(.22,1,.36,1) both; }
                .da-fade { animation: daFade .2s ease-out both; }
                .da-pop { animation: daPop .25s cubic-bezier(.22,1,.36,1) both; }
                @media (prefers-reduced-motion: reduce) {
                    .da-fade-up, .da-fade, .da-pop { animation: none !important; }
                }
            `}</style>

            {/* TAB NAVIGATION */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 bg-white rounded-2xl p-1.5 border border-slate-200 shadow-sm">
                {[
                    { id: 'utama', label: 'Main Data Agen', icon: 'users', locked: false },
                    { id: 'karyawan', label: 'Data Finansial & Operasional', icon: 'wallet', locked: !isPinUnlocked },
                    { id: 'jadwal', label: 'Jadwal Kerja Staff', icon: 'calendar', locked: !isPinUnlocked },
                ].map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => handleSwitchTab(tab.id)}
                        className={`flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl text-[13px] font-semibold transition-all duration-200 active:scale-[0.98] ${
                            activeMainTab === tab.id
                                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20'
                                : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                        }`}
                    >
                        <Icon name={tab.icon} className="w-[18px] h-[18px]" />
                        <span>{tab.label}</span>
                        {tab.locked && (
                            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                activeMainTab === tab.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                            }`}>
                                <Icon name="lock" className="w-3 h-3" />
                                Terkunci
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* MAIN TAB: AGEN */}
            {activeMainTab === 'utama' && (
                <div className="space-y-5 da-fade-up">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2 relative overflow-hidden bg-gradient-to-br from-[#1e293b] to-[#334155] p-6 sm:p-7 rounded-2xl text-white shadow-xl border border-slate-700">
                            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-blue-500/10 pointer-events-none"></div>
                            <div className="relative flex items-start gap-4">
                                <div className="w-12 h-12 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center text-blue-200">
                                    <Icon name="wallet" className="w-6 h-6" />
                                </div>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                                        <p className="text-[11px] font-semibold text-slate-300 uppercase tracking-widest">Total Saldo Cloud</p>
                                    </div>
                                    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight tabular-nums">
                                        {loading ? (
                                            <span className="inline-block w-48 h-8 bg-slate-700 rounded animate-pulse"></span>
                                        ) : (
                                            formatRP(grandTotalSaldo)
                                        )}
                                    </h2>
                                    <p className="text-xs text-slate-400 mt-2 flex items-center gap-1.5">
                                        <Icon name="users" className="w-3.5 h-3.5" />
                                        {loading ? 'Memuat data agen...' : `${agents.length} agen terdaftar`}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-center gap-3">
                            <div>
                                <p className="text-sm font-bold text-slate-800">Kelola Agen</p>
                                <p className="text-xs text-slate-400 mt-0.5">Tambahkan agen baru untuk mulai memantau saldonya.</p>
                            </div>
                            <button
                                onClick={() => { setIsEditing(false); setIsModalOpen(true); setFormData({id:'', name:'', phone:'', balance:''}) }}
                                className="bg-blue-600 hover:bg-blue-700 active:scale-95 text-white px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-200 shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2"
                            >
                                <Icon name="userPlus" className="w-[18px] h-[18px]" />
                                <span>Tambah Agen Baru</span>
                            </button>
                        </div>
                    </div>

                    <div className="relative">
                        <Icon name="search" className="w-[18px] h-[18px] absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Cari agen..."
                            value={searchTerm}
                            className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-slate-200 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 shadow-sm bg-white text-sm font-medium text-slate-800 placeholder:text-slate-400 transition-all duration-200"
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {[1, 2, 3, 4, 5, 6].map(i => <AgentSkeleton key={i} />)}
                        </div>
                    ) : filteredAgents.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-dashed border-slate-300">
                            <EmptyState icon="users" text={searchTerm ? 'Agen yang dicari tidak ditemukan.' : 'Belum ada data agen.'} />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {filteredAgents.map(agent => (
                                <AgentCard
                                    key={agent.id}
                                    agent={agent}
                                    onEdit={handleOpenEditAgent}
                                    onDelete={handleDeleteAgent}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* TAB: OPERATIONAL & FINANCIAL */}
            {activeMainTab === 'karyawan' && isPinUnlocked && (
                <div className="space-y-6 da-fade-up">
                    <SectionHeader
                        icon="wallet"
                        title="Laporan Operasional & Finansial (I F Y)"
                        subtitle="Laba mengambil otomatis dari rekapan harian. Setiap perubahan tersimpan otomatis."
                    >
                        <button
                            onClick={handleExportExcel}
                            className="bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white px-4 py-2.5 rounded-xl font-semibold text-sm shadow-md shadow-emerald-600/20 transition-all duration-200 flex items-center gap-2"
                        >
                            <Icon name="download" className="w-4 h-4" />
                            Export Excel (.xlsx)
                        </button>

                        <button
                            onClick={() => {
                                setIsOpEditing(false);
                                setOpFormData({
                                    id: '',
                                    name: '',
                                    persen: 0,
                                    nominal: 0
                                });
                                setIsOpModalOpen(true);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 active:scale-95 text-white px-4 py-2.5 rounded-xl font-semibold text-sm shadow-md shadow-blue-600/20 transition-all duration-200 flex items-center gap-2"
                        >
                            <Icon name="plus" className="w-4 h-4" />
                            Tambah Operasional
                        </button>
                    </SectionHeader>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                        {/* LEFT: OPERATIONAL */}
                        <div className="lg:col-span-7 bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                                        <Icon name="layers" className="w-[18px] h-[18px]" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold text-slate-800 leading-tight">Laporan Operasional</h3>
                                        <p className="text-xs text-slate-400">Pembagian persen &amp; nominal bersih per orang</p>
                                    </div>
                                </div>
                                <span className="text-[11px] font-semibold bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                                    {computedLaporanOperasional.length} data
                                </span>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse text-xs">
                                    <thead>
                                        <tr className="bg-slate-50 text-slate-500 text-[11px] font-semibold uppercase tracking-wide">
                                            <th className="py-2.5 px-4">Nama / Inisial</th>
                                            <th className="py-2.5 px-2 text-center w-28">Persen</th>
                                            <th className="py-2.5 px-4 text-right">Nominal <span className="normal-case font-normal text-slate-400">(setelah kasbon)</span></th>
                                            <th className="py-2.5 px-3 w-24 text-right">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {computedLaporanOperasional.map((item) => (
                                            <OpTableRow
                                                key={item.id}
                                                item={item}
                                                onPersenChange={handlePersenChange}
                                                onEdit={handleOpenEditOp}
                                                onDelete={handleDeleteOpData}
                                                matchingKasbon={kasbonMap.get(item.name.toUpperCase())}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                                {computedLaporanOperasional.length === 0 && <EmptyState text="Belum ada data operasional." />}
                            </div>
                        </div>

                        {/* RIGHT: FINANCIAL & KASBON */}
                        <div className="lg:col-span-5 bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                            {/* Total Laba */}
                            <div className="p-5 bg-gradient-to-br from-emerald-50 to-white border-b border-slate-100">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2 text-emerald-800">
                                        <Icon name="trendingUp" className="w-[18px] h-[18px]" />
                                        <span className="text-xs font-bold uppercase tracking-wide">Total Laba</span>
                                    </div>
                                    <span className="text-[11px] text-emerald-700/80 font-medium">otomatis rekapan.js</span>
                                </div>
                                <div className="flex items-center gap-2 bg-white border border-emerald-300 rounded-xl px-3.5 py-1.5 focus-within:ring-4 focus-within:ring-emerald-400/15 focus-within:border-emerald-500 transition-all duration-200">
                                    <span className="text-sm font-bold text-emerald-700">Rp</span>
                                    <input
                                        type="text"
                                        className="w-full text-right text-xl font-bold bg-transparent tabular-nums text-emerald-900 outline-none py-1"
                                        value={formatInputWithDots(manualLaba)}
                                        onChange={(e) => {
                                            const newLaba = parseRawNumber(e.target.value);
                                            setManualLaba(newLaba);
                                            saveGlobalConfigToCloud(newLaba, undefined, undefined);
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Pembagian */}
                            <div className="px-5 pt-4 pb-1">
                                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Laporan Finansial &amp; Pembagian</p>
                            </div>
                            <div className="px-5 divide-y divide-slate-100">
                                {[
                                    { icon: 'briefcase', tile: 'bg-slate-100 text-slate-600', label: 'Total Gaji (I F Y)', note: 'I, F, Y (15%)', value: formatRP(totalGajiInisialGross), valueClass: 'text-slate-800' },
                                    { icon: 'heart', tile: 'bg-emerald-50 text-emerald-600', label: 'Sedekah', note: '5% Laba', value: formatRP(nominalSedekah), valueClass: 'text-emerald-700' },
                                    { icon: 'landmark', tile: 'bg-blue-50 text-blue-600', label: 'Investasi', note: '20% Laba', value: formatRP(nominalInvestasi), valueClass: 'text-blue-700' },
                                ].map((row) => (
                                    <div key={row.label} className="flex items-center gap-3 py-3">
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${row.tile}`}>
                                            <Icon name={row.icon} className="w-[18px] h-[18px]" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-slate-800 leading-tight">{row.label}</p>
                                            <p className="text-[11px] text-slate-400">{row.note}</p>
                                        </div>
                                        <p className={`text-sm font-bold tabular-nums ${row.valueClass}`}>{row.value}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Skema Kasbon */}
                            <div className="m-5 p-4 rounded-xl bg-amber-50/70 border border-amber-200">
                                <div className="flex items-center justify-between gap-2 mb-3">
                                    <div className="flex items-center gap-2 text-amber-900">
                                        <Icon name="calculator" className="w-[18px] h-[18px]" />
                                        <span className="text-xs font-bold uppercase tracking-wide">Kalkulasi Skema Kasbon Baru</span>
                                    </div>
                                    <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold bg-white text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                        Auto save
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                                    <div className="sm:col-span-3">
                                        <label className="block text-[11px] font-semibold text-slate-500 mb-1">Total Nominal Kasbon</label>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-xs text-slate-400 font-semibold">Rp</span>
                                            <input
                                                type="text"
                                                className={`${UI_CELL_INPUT} text-right`}
                                                value={formatInputWithDots(kasbonSkema.totalKasbonBaru)}
                                                onChange={(e) => {
                                                    const newSkema = { ...kasbonSkema, totalKasbonBaru: parseRawNumber(e.target.value) };
                                                    setKasbonSkema(newSkema);
                                                    saveGlobalConfigToCloud(undefined, undefined, newSkema);
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="sm:col-span-1">
                                        <label className="block text-[11px] font-semibold text-slate-500 mb-1">Dibagi (Org)</label>
                                        <input
                                            type="number"
                                            className={`${UI_CELL_INPUT} text-center`}
                                            value={kasbonSkema.jumlahPembagi}
                                            onChange={(e) => {
                                                const newSkema = { ...kasbonSkema, jumlahPembagi: parseInt(e.target.value, 10) || 1 };
                                                setKasbonSkema(newSkema);
                                                saveGlobalConfigToCloud(undefined, undefined, newSkema);
                                            }}
                                        />
                                    </div>

                                    <div className="sm:col-span-1">
                                        <label className="block text-[11px] font-semibold text-slate-500 mb-1">Bayar (x)</label>
                                        <input
                                            type="number"
                                            className={`${UI_CELL_INPUT} text-center`}
                                            value={kasbonSkema.jumlahCicilan}
                                            onChange={(e) => {
                                                const newSkema = { ...kasbonSkema, jumlahCicilan: parseInt(e.target.value, 10) || 1 };
                                                setKasbonSkema(newSkema);
                                                saveGlobalConfigToCloud(undefined, undefined, newSkema);
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="mt-3 grid grid-cols-2 gap-px bg-amber-200 rounded-lg overflow-hidden text-xs">
                                    <div className="bg-white px-3 py-2">
                                        <p className="text-[11px] text-slate-500">Beban / Orang</p>
                                        <p className="font-bold text-amber-900 tabular-nums">{formatRP(hasilKasbonPerOrangTotal)}</p>
                                    </div>
                                    <div className="bg-white px-3 py-2">
                                        <p className="text-[11px] text-slate-500">Cicilan / Bayar</p>
                                        <p className="font-bold text-emerald-700 tabular-nums">{formatRP(hasilCicilanPerOrangPerBayar)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Daftar Kasbon */}
                            <div className="px-5 pb-2">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Kasbon / Piutang (I F Y)</p>
                                    <p className="text-xs font-bold text-amber-700 tabular-nums">Total: {formatRP(totalKasbon)}</p>
                                </div>
                                {kasbonList.length === 0 ? (
                                    <p className="text-xs text-slate-400 py-3 text-center">Belum ada data kasbon.</p>
                                ) : (
                                    <div className="space-y-2">
                                        {kasbonList.map((kasbon) => (
                                            <div key={kasbon.id} className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2 hover:border-amber-200 transition-colors duration-200">
                                                <span className="uppercase font-bold text-xs text-amber-900 bg-amber-100 px-2.5 py-1 rounded-md min-w-[3rem] text-center">{kasbon.nama}</span>
                                                <span className="text-xs text-slate-400 font-semibold ml-auto">Rp</span>
                                                <input
                                                    type="text"
                                                    className="w-28 text-right bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-slate-800 tabular-nums outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-400/10 transition-all duration-200"
                                                    value={formatInputWithDots(kasbon.nominal)}
                                                    onChange={(e) => handleKasbonNominalChange(kasbon.id, e.target.value)}
                                                />
                                                <button
                                                    onClick={() => handleDeleteKasbon(kasbon.id)}
                                                    title="Hapus kasbon"
                                                    aria-label="Hapus kasbon"
                                                    className={`${UI_ICON_BTN} hover:bg-red-50 hover:text-red-600 hover:border-red-100`}
                                                >
                                                    <Icon name="trash" className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Gaji Bersih */}
                            <div className="mx-5 my-4 flex items-center justify-between gap-3 bg-[#1e293b] text-white rounded-xl px-4 py-3.5">
                                <div>
                                    <p className="text-sm font-bold leading-tight">Gaji Bersih Total (I F Y)</p>
                                    <p className="text-[11px] text-slate-400">Sudah memotong kasbon</p>
                                </div>
                                <p className="text-lg font-bold tabular-nums text-emerald-300">{formatRP(totalGajiInisialNett)}</p>
                            </div>

                            {/* Modal */}
                            <div className="px-5 pb-5 divide-y divide-slate-100">
                                <div className="flex items-center gap-3 py-3">
                                    <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                                        <Icon name="coins" className="w-[18px] h-[18px]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-slate-800 leading-tight">Tambah Modal (25%)</p>
                                        <p className="text-[11px] text-slate-400">25% Laba</p>
                                    </div>
                                    <p className="text-sm font-bold tabular-nums text-indigo-700">{formatRP(tambahModal)}</p>
                                </div>
                                <div className="pt-3">
                                    <label className="flex items-center justify-between text-xs mb-1.5">
                                        <span className="font-semibold text-emerald-900">Modal Bulan Depan</span>
                                        <span className="text-[11px] text-slate-400">dapat diedit</span>
                                    </label>
                                    <div className="flex items-center gap-2 bg-emerald-50/60 border border-emerald-300 rounded-xl px-3.5 py-1 focus-within:ring-4 focus-within:ring-emerald-400/15 focus-within:border-emerald-500 transition-all duration-200">
                                        <span className="text-sm font-bold text-emerald-700">Rp</span>
                                        <input
                                            type="text"
                                            className="w-full text-right text-base font-bold bg-transparent tabular-nums text-emerald-900 outline-none py-1.5"
                                            value={formatInputWithDots(manualModalBulanDepan)}
                                            onChange={(e) => {
                                                const newModal = parseRawNumber(e.target.value);
                                                setManualModalBulanDepan(newModal);
                                                saveGlobalConfigToCloud(undefined, newModal, undefined);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ALOKASI KAS KARYAWAN 5% */}
                    <div className="bg-gradient-to-r from-purple-900 to-indigo-900 rounded-2xl p-5 sm:p-6 text-white shadow-lg border border-purple-700">
                        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-5">
                            <div className="flex items-start gap-4">
                                <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center text-purple-200">
                                    <Icon name="users" className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-base leading-tight">Alokasi Kas Karyawan (5% Laba)</h3>
                                    <p className="text-xs text-purple-200 mt-1">
                                        Kalkulasi dari 5% Laba dikurangi total terpakai gaji karyawan tambahan.
                                    </p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-3">
                                <div className="bg-white/10 border border-white/15 rounded-xl px-4 py-2.5">
                                    <p className="text-[11px] text-purple-200 font-semibold">Total Alokasi (5%)</p>
                                    <p className="text-base font-bold tabular-nums text-emerald-300">{formatRP(nominalAlokasiKaryawan)}</p>
                                </div>
                                <div className="hidden sm:flex w-6 h-6 rounded-full bg-white/10 items-center justify-center text-purple-200 text-sm font-bold">−</div>
                                <div className="bg-white/10 border border-white/15 rounded-xl px-4 py-2.5">
                                    <p className="text-[11px] text-purple-200 font-semibold">Gaji Terpakai</p>
                                    <p className="text-base font-bold tabular-nums text-amber-300">{formatRP(totalSemuaKaryawanDiterima)}</p>
                                </div>
                                <div className="hidden sm:flex w-6 h-6 rounded-full bg-white/10 items-center justify-center text-purple-200 text-sm font-bold">=</div>
                                <div className="bg-purple-950/80 border border-purple-400/60 rounded-xl px-4 py-2.5">
                                    <p className="text-[11px] text-purple-300 font-semibold">Sisa Alokasi</p>
                                    <p className={`text-lg font-bold tabular-nums ${sisaAlokasiKasKaryawan >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {formatRP(sisaAlokasiKasKaryawan)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* EMPLOYEE EXTRA SALARY DETAILS */}
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                                    <Icon name="briefcase" className="w-[18px] h-[18px]" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-slate-800 leading-tight">Detail Salary Karyawan Tetap &amp; Tambahan</h3>
                                    <p className="text-xs text-slate-400">Gaji pokok harian ditambah bonus dari laba</p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setSalaryFormData({ id: '', nama: '', hariKerja: 26, ratePerHari: 25000, bonusPersen: 0.5 });
                                    setIsSalaryModalOpen(true);
                                }}
                                className="bg-purple-600 hover:bg-purple-700 active:scale-95 text-white px-4 py-2.5 rounded-xl font-semibold text-sm shadow-md shadow-purple-600/20 transition-all duration-200 flex items-center justify-center gap-2"
                            >
                                <Icon name="userPlus" className="w-4 h-4" />
                                Tambah Karyawan Baru
                            </button>
                        </div>

                        <div className="p-5">
                            {karyawanSalaryList.length === 0 ? (
                                <EmptyState icon="users" text="Belum ada data karyawan." />
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                    {karyawanSalaryList.map((karyawan) => {
                                        const subtotalGapok = (karyawan.hariKerja || 0) * (karyawan.ratePerHari || 0);
                                        const subtotalBonus = (manualLaba * (karyawan.bonusPersen || 0)) / 100;
                                        const totalDiterima = subtotalGapok + subtotalBonus;

                                        return (
                                            <div key={karyawan.id} className="rounded-xl border border-slate-200 hover:border-purple-200 hover:shadow-md transition-all duration-300 overflow-hidden bg-white">
                                                <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border-b border-slate-100">
                                                    <div className="w-9 h-9 rounded-lg bg-purple-100 text-purple-700 font-bold text-sm flex items-center justify-center uppercase">
                                                        {(karyawan.nama || '?').toString().trim().charAt(0) || '?'}
                                                    </div>
                                                    <p className="flex-1 min-w-0 truncate uppercase font-bold text-slate-800 text-sm">{karyawan.nama}</p>
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            onClick={() => {
                                                                setSalaryFormData({
                                                                    id: karyawan.id,
                                                                    nama: karyawan.nama,
                                                                    hariKerja: karyawan.hariKerja,
                                                                    ratePerHari: formatInputWithDots(karyawan.ratePerHari),
                                                                    bonusPersen: karyawan.bonusPersen
                                                                });
                                                                setIsSalaryModalOpen(true);
                                                            }}
                                                            title="Edit karyawan"
                                                            aria-label="Edit karyawan"
                                                            className={`${UI_ICON_BTN} hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100`}
                                                        >
                                                            <Icon name="edit" className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteSalaryKaryawan(karyawan.id)}
                                                            title="Hapus karyawan"
                                                            aria-label="Hapus karyawan"
                                                            className={`${UI_ICON_BTN} hover:bg-red-50 hover:text-red-600 hover:border-red-100`}
                                                        >
                                                            <Icon name="trash" className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="px-4 py-3 space-y-3">
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div>
                                                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Gapok (Harian)</p>
                                                            <p className="text-xs text-slate-500 tabular-nums">{karyawan.hariKerja} hari × {formatRP(karyawan.ratePerHari)}</p>
                                                        </div>
                                                        <p className="text-sm font-bold text-slate-800 tabular-nums">{formatRP(subtotalGapok)}</p>
                                                    </div>
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div>
                                                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Bonus Laba</p>
                                                            <p className="text-xs text-slate-500 tabular-nums">{karyawan.bonusPersen}% × {formatRP(manualLaba)}</p>
                                                        </div>
                                                        <p className="text-sm font-bold text-emerald-700 tabular-nums">{formatRP(subtotalBonus)}</p>
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between px-4 py-3 bg-purple-50 border-t border-purple-100">
                                                    <p className="text-xs font-semibold text-purple-900">Total Diterima</p>
                                                    <p className="text-base font-bold text-purple-800 tabular-nums">{formatRP(totalDiterima)}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* TAB: WORK SCHEDULE */}
            {activeMainTab === 'jadwal' && isPinUnlocked && (
                <div className="space-y-6 da-fade-up">
                    <SectionHeader
                        icon="calendar"
                        title="Jadwal Kerja Staff / Karyawan"
                        subtitle="Atur shift harian karyawan (Pagi, Siang, Malam, Libur) dengan indikator jam kerja & tanggal manual."
                    >
                        <button
                            onClick={handleSendJadwalToTelegram}
                            disabled={isSendingTelegram}
                            className="bg-sky-500 hover:bg-sky-600 active:scale-95 text-white px-4 py-2.5 rounded-xl font-semibold text-sm shadow-md shadow-sky-500/20 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSendingTelegram ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                    <span>Mengirim Gambar...</span>
                                </>
                            ) : (
                                <>
                                    <Icon name="send" className="w-4 h-4" />
                                    <span>Kirim ke Telegram</span>
                                </>
                            )}
                        </button>

                        <button
                            onClick={() => setIsShiftConfigModalOpen(true)}
                            className="bg-white hover:bg-amber-50 text-slate-700 hover:text-amber-700 border border-slate-200 hover:border-amber-300 active:scale-95 px-4 py-2.5 rounded-xl font-semibold text-sm shadow-sm transition-all duration-200 flex items-center gap-2"
                        >
                            <Icon name="clock" className="w-4 h-4" />
                            Edit Jam Kerja
                        </button>

                        <button
                            onClick={() => {
                                setJadwalFormData({
                                    id: '', nama: '', senin: 'Pagi', selasa: 'Pagi', rabu: 'Pagi', kamis: 'Pagi', jumat: 'Pagi', sabtu: 'Libur', minggu: 'Libur'
                                });
                                setIsJadwalModalOpen(true);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 active:scale-95 text-white px-4 py-2.5 rounded-xl font-semibold text-sm shadow-md shadow-blue-600/20 transition-all duration-200 flex items-center gap-2"
                        >
                            <Icon name="plus" className="w-4 h-4" />
                            Tambah Karyawan Jadwal
                        </button>
                    </SectionHeader>

                    {/* Pembungkus scroll agar layar kecil bisa digeser tanpa merusak area export gambar */}
                    <div className="overflow-x-auto pb-1">
                    {/* WRAPPER RENDER GAMBAR */}
                    <div ref={jadwalContainerRef} className="p-6 bg-white rounded-3xl border border-slate-200 space-y-6 shadow-sm min-w-[1000px]">

                        {/* HEADER TAMPILAN GAMBAR */}
                        <div className="text-center pb-4 border-b border-slate-200">
                            <h3 className="text-xl font-bold text-slate-800 uppercase tracking-wider">JADWAL OPERASIONAL &amp; SHIFT KERJA STAFF</h3>
                            <p className="text-xs text-blue-600 font-bold uppercase mt-1.5 tracking-widest">
                                PERIODE MINGGUAN: {displayDates.senin} s/d {displayDates.minggu}
                            </p>
                            <p className="text-[11px] text-slate-400 font-medium mt-0.5">Sistem Management Operasional Agen</p>
                        </div>

                        {/* SHIFT INDICATOR BADGES */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[
                                { icon: 'sun', name: 'Shift Pagi', time: shiftTimes.pagi, box: 'bg-blue-50 border-blue-200', tile: 'bg-blue-100 text-blue-700', nameCls: 'text-blue-900', timeCls: 'text-blue-700' },
                                { icon: 'sunset', name: 'Shift Siang', time: shiftTimes.siang || shiftTimes.sore, box: 'bg-amber-50 border-amber-200', tile: 'bg-amber-100 text-amber-700', nameCls: 'text-amber-900', timeCls: 'text-amber-700' },
                                { icon: 'moon', name: 'Shift Malam', time: shiftTimes.malam, box: 'bg-indigo-950 border-indigo-800', tile: 'bg-indigo-800 text-indigo-100', nameCls: 'text-indigo-100', timeCls: 'text-indigo-300' },
                                { icon: 'coffee', name: 'Libur', time: shiftTimes.libur, box: 'bg-rose-50 border-rose-200', tile: 'bg-rose-100 text-rose-700', nameCls: 'text-rose-900', timeCls: 'text-rose-700' },
                            ].map((s) => (
                                <div key={s.name} className={`${s.box} border p-3.5 rounded-xl flex items-center gap-3`}>
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.tile}`}>
                                        <Icon name={s.icon} className="w-5 h-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className={`font-bold text-xs uppercase tracking-wide ${s.nameCls}`}>{s.name}</p>
                                        <p className={`text-[12px] font-semibold mt-0.5 ${s.timeCls}`}>{s.time}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* WEEKLY SHIFT MATRIX WITH DATE HEADER CLASS */}
                        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                            <div className="bg-[#1e293b] text-white py-3.5 px-5 flex justify-between items-center gap-3">
                                <span className="font-bold text-sm uppercase tracking-wider">MATRIKS JADWAL KERJA MINGGUAN</span>
                                <span className="text-[11px] bg-slate-700 px-3 py-1 rounded-full text-slate-200 font-medium">Pagi | Siang | Malam | Libur</span>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs border-collapse">
                                    <thead>
                                        <tr className="bg-slate-800 text-white font-bold text-center uppercase tracking-wider">
                                            <th className="p-3 border-r border-slate-700 w-32 align-middle text-left">Nama / Inisial</th>

                                            {JADWAL_DAYS.map((d) => (
                                                <th key={d.key} className={`p-2.5 border-r border-slate-700 ${d.th}`}>
                                                    <div className={`text-[11px] font-bold mb-1.5 tracking-wider uppercase ${d.lbl}`}>{d.label}</div>
                                                    <input
                                                        type="text"
                                                        className={`date-header-input w-24 text-center text-xs font-mono font-semibold border rounded-md px-1.5 py-1 outline-none focus:ring-1 transition-all duration-200 ${d.inp}`}
                                                        value={displayDates[d.key]}
                                                        onChange={(e) => handleCustomDateChange(d.key, e.target.value)}
                                                        placeholder="DD/MM/YYYY"
                                                    />
                                                </th>
                                            ))}

                                            <th className="p-3 w-28 no-export align-middle">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-center">
                                        {jadwalKerjaList.map((j) => (
                                            <tr key={j.id} className="hover:bg-slate-50/80 transition-colors duration-150">
                                                <td className="p-3 bg-slate-50 text-slate-900 font-bold text-sm uppercase border-r border-slate-100 text-left">
                                                    {j.nama}
                                                </td>
                                                {JADWAL_DAYS.map((d) => (
                                                    <td key={d.key} className={`p-3 border-r border-slate-100 ${d.td}`}>
                                                        <ShiftBadge shift={j[d.key]} times={shiftTimes} />
                                                    </td>
                                                ))}
                                                <td className="p-3 no-export">
                                                    <div className="flex items-center justify-center gap-1">
                                                        <button
                                                            onClick={() => {
                                                                setJadwalFormData({
                                                                    id: j.id,
                                                                    nama: j.nama || '',
                                                                    senin: j.senin || 'Pagi',
                                                                    selasa: j.selasa || 'Pagi',
                                                                    rabu: j.rabu || 'Pagi',
                                                                    kamis: j.kamis || 'Pagi',
                                                                    jumat: j.jumat || 'Pagi',
                                                                    sabtu: j.sabtu || 'Libur',
                                                                    minggu: j.minggu || 'Libur'
                                                                });
                                                                setIsJadwalModalOpen(true);
                                                            }}
                                                            title="Edit jadwal"
                                                            aria-label="Edit jadwal"
                                                            className={`${UI_ICON_BTN} hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100`}
                                                        >
                                                            <Icon name="edit" className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteJadwal(j.id)}
                                                            title="Hapus jadwal"
                                                            aria-label="Hapus jadwal"
                                                            className={`${UI_ICON_BTN} hover:bg-red-50 hover:text-red-600 hover:border-red-100`}
                                                        >
                                                            <Icon name="trash" className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {jadwalKerjaList.length === 0 && <EmptyState icon="calendar" text="Belum ada jadwal staff." />}
                            </div>
                        </div>
                    </div>
                    </div>
                </div>
            )}

            {/* MODAL CONFIRMATION DELETE */}
            {deleteModal.show && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[150] flex items-center justify-center p-4 transition-all duration-300">
                    <div className="bg-white p-8 rounded-[2.5rem] w-full max-w-sm shadow-2xl text-center transform transition-all duration-300 scale-100 border border-slate-100">
                        <h3 className="text-base font-black text-slate-800 uppercase mb-2 tracking-wide">
                            {deleteModal.title}
                        </h3>
                        <p className="text-xs text-slate-500 font-medium mb-6 leading-relaxed">
                            {deleteModal.message}
                        </p>
                        
                        <div className="flex gap-3">
                            <button 
                                type="button" 
                                onClick={closeDeleteConfirm} 
                                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-600 py-3.5 rounded-2xl font-black uppercase text-[11px] transition-all duration-200 active:scale-95"
                            >
                                Batal
                            </button>
                            <button 
                                type="button" 
                                onClick={async () => {
                                    if (deleteModal.onConfirm) {
                                        await deleteModal.onConfirm();
                                    }
                                    closeDeleteConfirm();
                                }} 
                                className="flex-1 bg-red-600 hover:bg-red-700 active:scale-95 text-white py-3.5 rounded-2xl font-black uppercase text-[11px] transition-all duration-200 shadow-lg shadow-red-200"
                            >
                                Ya, Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL CONFIG EDIT SHIFT TIMES */}
            {isShiftConfigModalOpen && (
                <ModalShell
                    z="z-[110]"
                    tone="amber"
                    icon="clock"
                    title="Pengaturan Jam Kerja Shift"
                    subtitle="Atur waktu mulai - selesai untuk setiap shift"
                    onClose={() => setIsShiftConfigModalOpen(false)}
                >
                    <form onSubmit={handleSaveShiftConfig} className="space-y-4">
                        {[
                            { key: 'pagi', label: 'Jam Shift Pagi', icon: 'sun', tile: 'bg-blue-50 text-blue-600', ring: 'focus:border-blue-500 focus:ring-blue-500/10', placeholder: '06.00 - 15.00 WIB', value: shiftTimes.pagi },
                            { key: 'siang', label: 'Jam Shift Siang', icon: 'sunset', tile: 'bg-amber-50 text-amber-600', ring: 'focus:border-amber-500 focus:ring-amber-500/10', placeholder: '12.00 - 21.00 WIB', value: shiftTimes.siang || shiftTimes.sore || '' },
                            { key: 'malam', label: 'Jam Shift Malam', icon: 'moon', tile: 'bg-indigo-50 text-indigo-600', ring: 'focus:border-indigo-500 focus:ring-indigo-500/10', placeholder: '23.00 - 08.00 WIB', value: shiftTimes.malam },
                            { key: 'libur', label: 'Keterangan Libur', icon: 'coffee', tile: 'bg-rose-50 text-rose-600', ring: 'focus:border-rose-500 focus:ring-rose-500/10', placeholder: 'OFF / Free Day', value: shiftTimes.libur },
                        ].map((f) => (
                            <div key={f.key}>
                                <label className={UI_LABEL}>{f.label}</label>
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${f.tile}`}>
                                        <Icon name={f.icon} className="w-[18px] h-[18px]" />
                                    </div>
                                    <input
                                        type="text"
                                        required
                                        className={`${UI_INPUT} ${f.ring}`}
                                        value={f.value}
                                        onChange={(e) => setShiftTimes({ ...shiftTimes, [f.key]: e.target.value })}
                                        placeholder={f.placeholder}
                                    />
                                </div>
                            </div>
                        ))}

                        <ModalFooter
                            tone="amber"
                            onCancel={() => setIsShiftConfigModalOpen(false)}
                            disabled={isSubmitting}
                            submitLabel={isSubmitting ? 'Simpan...' : 'Simpan Shift'}
                        />
                    </form>
                </ModalShell>
            )}

            {/* MODAL JADWAL KERJA */}
            {isJadwalModalOpen && (
                <ModalShell
                    tone="blue"
                    icon="calendar"
                    maxW="max-w-lg"
                    title={jadwalFormData.id ? 'Edit Jadwal Staff' : 'Tambah Jadwal Staff Baru'}
                    subtitle="Pilih shift untuk setiap hari dalam seminggu"
                    onClose={() => setIsJadwalModalOpen(false)}
                >
                    <form onSubmit={handleSaveJadwal} className="space-y-5">
                        <div>
                            <label className={UI_LABEL}>Nama Staff / Karyawan</label>
                            <input
                                type="text"
                                required
                                className={`${UI_INPUT} uppercase`}
                                value={jadwalFormData.nama}
                                onChange={(e) => setJadwalFormData({ ...jadwalFormData, nama: e.target.value })}
                                placeholder="Masukkan Nama Staff..."
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            {['senin', 'selasa', 'rabu', 'kamis', 'jumat', 'sabtu', 'minggu'].map((hari) => (
                                <div key={hari}>
                                    <label className={UI_LABEL}>{hari}</label>
                                    <select
                                        className={`${UI_INPUT} cursor-pointer`}
                                        value={jadwalFormData[hari] || 'Pagi'}
                                        onChange={(e) => setJadwalFormData({ ...jadwalFormData, [hari]: e.target.value })}
                                    >
                                        <option value="Pagi">Pagi</option>
                                        <option value="Siang">Siang</option>
                                        <option value="Malam">Malam</option>
                                        <option value="Libur">Libur</option>
                                    </select>
                                </div>
                            ))}
                        </div>

                        <ModalFooter
                            tone="blue"
                            onCancel={() => setIsJadwalModalOpen(false)}
                            disabled={isSubmitting}
                            submitLabel={isSubmitting ? 'Menyimpan...' : 'Simpan Jadwal'}
                        />
                    </form>
                </ModalShell>
            )}

            {/* MODAL PIN ACCESS */}
            {showPinModal && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 transition-all duration-300">
                    <div className="bg-white p-8 rounded-[3rem] w-full max-w-sm shadow-2xl text-center transform transition-transform duration-300 scale-100 border border-slate-100">
                        <h3 className="text-lg font-black text-slate-800 uppercase mb-2">Akses Terkunci</h3>
                        <p className="text-xs text-slate-400 font-medium mb-6">Masukkan PIN keamanan untuk mengakses menu ini.</p>
                        <form onSubmit={handleVerifyPin} className="space-y-4">
                            <input 
                                type="password" 
                                maxLength={6}
                                autoFocus
                                required
                                value={inputPin}
                                onChange={(e) => setInputPin(e.target.value)}
                                className="w-full text-center text-2xl tracking-[0.5em] font-black p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                                placeholder="******"
                            />
                            <div className="flex gap-3">
                                <button 
                                    type="button" 
                                    onClick={() => setShowPinModal(false)} 
                                    className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-600 py-3.5 rounded-2xl font-bold uppercase text-xs transition-all active:scale-95"
                                >
                                    Batal
                                </button>
                                <button 
                                    type="submit" 
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-2xl font-bold uppercase text-xs transition-all active:scale-95 shadow-lg shadow-blue-200"
                                >
                                    Buka Akses
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL OPERASIONAL */}
            {isOpModalOpen && (
                <ModalShell
                    tone="blue"
                    icon="layers"
                    title={isOpEditing ? 'Edit Baris Operasional' : 'Tambah Operasional Baru'}
                    subtitle="Isi nama, persen, dan nominal manual"
                    onClose={() => setIsOpModalOpen(false)}
                >
                    <form onSubmit={handleSaveOpData} className="space-y-4">
                        <div>
                            <label className={UI_LABEL}>Nama / Inisial</label>
                            <input
                                type="text"
                                required
                                className={`${UI_INPUT} uppercase`}
                                value={opFormData.name}
                                onChange={(e) => setOpFormData({ ...opFormData, name: e.target.value })}
                                placeholder="Contoh: FARID / KARYAWAN"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={UI_LABEL}>Persen (%)</label>
                                <input
                                    type="text"
                                    className={UI_INPUT}
                                    value={opFormData.persen}
                                    onChange={(e) => setOpFormData({ ...opFormData, persen: e.target.value })}
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className={UI_LABEL}>Nominal Manual (Rp)</label>
                                <input
                                    type="text"
                                    className={`${UI_INPUT} tabular-nums`}
                                    value={opFormData.nominal}
                                    onChange={(e) => setOpFormData({ ...opFormData, nominal: formatInputWithDots(e.target.value) })}
                                    placeholder="0"
                                />
                            </div>
                        </div>

                        <ModalFooter
                            tone="blue"
                            onCancel={() => setIsOpModalOpen(false)}
                            disabled={isSubmitting}
                            submitLabel={isSubmitting ? 'Menyimpan...' : 'Simpan Data'}
                        />
                    </form>
                </ModalShell>
            )}

            {/* MODAL SALARY KARYAWAN */}
            {isSalaryModalOpen && (
                <ModalShell
                    tone="purple"
                    icon="briefcase"
                    title={salaryFormData.id ? 'Edit Karyawan' : 'Tambah Karyawan Baru'}
                    subtitle="Gaji pokok harian dan bonus laba"
                    onClose={() => setIsSalaryModalOpen(false)}
                >
                    <form onSubmit={handleSaveSalaryKaryawan} className="space-y-4">
                        <div>
                            <label className={UI_LABEL}>Nama Karyawan</label>
                            <input
                                type="text"
                                required
                                className={`${UI_INPUT} uppercase focus:border-purple-500 focus:ring-purple-500/10`}
                                value={salaryFormData.nama}
                                onChange={(e) => setSalaryFormData({ ...salaryFormData, nama: e.target.value })}
                                placeholder="Masukkan Nama..."
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={UI_LABEL}>Hari Kerja</label>
                                <input
                                    type="number"
                                    required
                                    className={`${UI_INPUT} focus:border-purple-500 focus:ring-purple-500/10`}
                                    value={salaryFormData.hariKerja}
                                    onChange={(e) => setSalaryFormData({ ...salaryFormData, hariKerja: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className={UI_LABEL}>Rate Per Hari (Rp)</label>
                                <input
                                    type="text"
                                    required
                                    className={`${UI_INPUT} tabular-nums focus:border-purple-500 focus:ring-purple-500/10`}
                                    value={salaryFormData.ratePerHari}
                                    onChange={(e) => setSalaryFormData({ ...salaryFormData, ratePerHari: formatInputWithDots(e.target.value) })}
                                />
                            </div>
                        </div>

                        <div>
                            <label className={UI_LABEL}>Bonus Laba (%)</label>
                            <input
                                type="text"
                                className={`${UI_INPUT} focus:border-purple-500 focus:ring-purple-500/10`}
                                value={salaryFormData.bonusPersen}
                                onChange={(e) => setSalaryFormData({ ...salaryFormData, bonusPersen: e.target.value })}
                                placeholder="0.5"
                            />
                        </div>

                        <ModalFooter
                            tone="purple"
                            onCancel={() => setIsSalaryModalOpen(false)}
                            disabled={isSubmitting}
                            submitLabel={isSubmitting ? 'Menyimpan...' : 'Simpan Karyawan'}
                        />
                    </form>
                </ModalShell>
            )}

            {/* MODAL AGEN */}
            {isModalOpen && (
                <ModalShell
                    tone="blue"
                    icon="userPlus"
                    title={isEditing ? 'Edit Data Agen' : 'Tambah Agen Baru'}
                    subtitle="Lengkapi data agen dan saldo awalnya"
                    onClose={() => setIsModalOpen(false)}
                >
                    <form onSubmit={handleSaveAgent} className="space-y-4">
                        <div>
                            <label className={UI_LABEL}>Nama Agen</label>
                            <input
                                type="text"
                                required
                                className={`${UI_INPUT} uppercase`}
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className={UI_LABEL}>Nomor HP / WhatsApp</label>
                            <input
                                type="text"
                                className={UI_INPUT}
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className={UI_LABEL}>Sisa Saldo (Rp)</label>
                            <input
                                type="text"
                                required
                                className={`${UI_INPUT} tabular-nums`}
                                value={formData.balance}
                                onChange={(e) => setFormData({ ...formData, balance: formatInputWithDots(e.target.value) })}
                            />
                        </div>
                        <ModalFooter
                            tone="blue"
                            onCancel={() => setIsModalOpen(false)}
                            disabled={isSubmitting}
                            submitLabel={isSubmitting ? 'Menyimpan...' : 'Simpan Data'}
                        />
                    </form>
                </ModalShell>
            )}
        </div>
    );
}